// One share (GET /admin/v1/sharing/shares/{kind}/{ref}, contracts/index-cards.md §5): the snapshot stored when it was
// created (a Logic Index share's value, tier, standing, strongest skill, streak, glow and whether the handle shows;
// a daily share's puzzle, mode, result, percentile and rank), the format, channel, language and when it was created
// and completed, the public link, its clicks, uniques, previews and app opens, clicks per day, and where the clicks
// came from (sources, platforms, in-app browsers). By default from the day it was created to today; ?from&to (UTC
// days) narrow it, as the Sharing page's links do.

import type { ReactNode } from "react";
import { Link, useParams, useSearchParams } from "react-router";
import { type ShareDetail, type ShareSnapshot, useShare } from "../../api/sharing";
import { ColumnChart } from "../../components/charts/charts";
import { Orb, ORB_NAMES } from "../../components/Orb";
import { Card, CategoryTag, Chip, ErrorState, KpiTile, Loading, PageHeader } from "../../components/ui";
import { formatDateTime, formatShortDate } from "../../lib/dates";
import { fmtInt, fmtPct } from "../../lib/format";
import { CHANNEL_LABEL, FORMAT_LABEL, IN_APP_LABEL, labelOf, PLATFORM_LABEL, SHARE_KIND_LABEL, SOURCE_LABEL, TIERS, tierName, tierOf } from "../../lib/sharing";
import { LOCALE_LABEL, type Locale } from "../../api/messaging";
import l from "../learn/learn.module.css";
import pages from "../pages.module.css";
import { ClickBreakdown, OwnerLink, rate, readout, TierCrest } from "./components";
import s from "./sharing.module.css";

const isDate = (v: string | null) => (v && /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : undefined);

export function ShareDetailPage() {
  const { kind = "", ref = "" } = useParams();
  const [params] = useSearchParams();
  const q = useShare(kind, ref, { from: isDate(params.get("from")), to: isDate(params.get("to")) });

  const header = (meta?: ReactNode, actions?: ReactNode) => (
    <PageHeader
      title={<span className={pages.monoTitle}>{ref}</span>}
      meta={
        <span className={l.statusLine}>
          <Link to="/sharing" className={l.back}>
            ← Sharing
          </Link>
          {meta}
        </span>
      }
      actions={actions}
    />
  );

  if (q.isPending) {
    return (
      <>
        {header()}
        <Loading rows={10} />
      </>
    );
  }
  if (q.isError) {
    return (
      <>
        {header()}
        <ErrorState error={q.error} onRetry={() => void q.refetch()} title={(q.error as { status?: number }).status === 404 ? "No such share" : "Couldn't load this share"} />
      </>
    );
  }

  const d = q.data;
  const sh = d.share;
  return (
    <>
      {header(
        <>
          <Chip tone={sh.kind === "index" ? "violet" : "info"}>{labelOf(SHARE_KIND_LABEL, sh.kind)}</Chip>
          <span className="mono">created {formatDateTime(sh.createdAt)}</span>
          <span className="mono">
            clicks {formatShortDate(d.from)} – {formatShortDate(d.to)} UTC
          </span>
        </>,
        <a href={sh.url} target="_blank" rel="noreferrer" className={s.external} data-testid="public-link">
          {sh.url.replace(/^https?:\/\//, "")} ↗
        </a>,
      )}
      <div className={s.stack}>
        <div className={s.detailTop}>
          <Hero share={sh} />
          <Card title="Snapshot" actions={<span className={pages.faint}>as stored when it was shared</span>}>
            <Snapshot share={sh} />
          </Card>
        </div>
        <Totals detail={d} />
        <Card title="Clicks per day" actions={<span className="mono">{fmtInt(d.totals.clicks)}</span>}>
          <ColumnChart
            label="Clicks per day"
            valueName="clicks"
            height={150}
            rows={d.days.map((x) => ({
              key: x.date,
              label: formatShortDate(x.date),
              value: x.clicks,
              notes: [`${fmtInt(x.uniques)} unique visitors`, `${fmtInt(x.previews)} previews`, `${fmtInt(x.opens)} app opens`],
            }))}
          />
        </Card>
        <Card title="Where the clicks came from">
          <div className={s.breakdowns} data-testid="share-breakdowns">
            <ClickBreakdown title="Sources" rows={d.sources} labels={SOURCE_LABEL} testId="share-sources" />
            <ClickBreakdown title="Platforms" rows={d.platforms} labels={PLATFORM_LABEL} testId="share-platforms" />
            <ClickBreakdown title="In-app browser" rows={d.inApp} labels={IN_APP_LABEL} testId="share-in-app" />
          </div>
        </Card>
      </div>
    </>
  );
}

/** The card's headline: the index in its tier's tile, or the daily puzzle. */
function Hero({ share: sh }: { share: ShareSnapshot }) {
  if (sh.kind === "index") {
    const tier = TIERS.find((t) => t.id === sh.tier) ?? (sh.value !== null ? tierOf(sh.value) : null);
    return (
      <KpiTile
        palette={tier?.tile ?? "violet"}
        label={
          <>
            Logic Index <small>{tier ? tierName(tier.id) : "—"}</small>
          </>
        }
        value={sh.value !== null ? String(sh.value) : "-"}
        foot={<span>{tier ? `${tier.name} · ${tier.min}–${tier.max}` : "no tier"}</span>}
      />
    );
  }
  return (
    <KpiTile
      palette={sh.mode === "hard" ? "magenta" : "violet"}
      label={
        <>
          Daily <small>{sh.mode === "hard" ? "Hard Mode" : "puzzle"}</small>
        </>
      }
      value={sh.puzzleNumber !== null ? String(sh.puzzleNumber) : "-"}
      foot={<span>{sh.correct === null ? "—" : sh.correct ? "solved" : "missed"}</span>}
    />
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="monoLabel">{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

const none = <span className={pages.faint}>—</span>;
const localeName = (l: string | null) => (l ? (LOCALE_LABEL[l as Locale] ?? l) : null);

function Snapshot({ share: sh }: { share: ShareSnapshot }) {
  return (
    <dl className={s.snapshot} data-testid="snapshot">
      <Fact label="Owner">
        <OwnerLink owner={sh.owner} />
      </Fact>
      {sh.kind === "index" ? (
        <>
          <Fact label="Index">
            <TierCrest tier={sh.tier} value={sh.value} />
          </Fact>
          <Fact label="Standing">
            {sh.betterThan !== null ? (
              <>
                Sharper than {fmtPct(Math.floor(sh.betterThan * 100) / 100)}
                {sh.players !== null ? <small>of {fmtInt(sh.players)} players</small> : null}
              </>
            ) : (
              <span className={pages.faint}>not shown (too few holders)</span>
            )}
          </Fact>
          <Fact label="Tier share">{sh.tierShare !== null ? `${fmtPct(sh.tierShare, 1)} reach ${tierName(sh.tier)}` : none}</Fact>
          <Fact label="Strongest">
            {sh.strongest ? (
              <>
                <CategoryTag category={sh.strongest.category} />
                <small>better than {fmtPct(sh.strongest.betterThan)}</small>
              </>
            ) : (
              none
            )}
          </Fact>
          <Fact label="Handle on the card">
            {sh.showHandle === null ? none : sh.showHandle ? (sh.owner?.handle ? `shown · @${sh.owner.handle}` : "shown") : "hidden"}
          </Fact>
          <Fact label="Glow">
            {sh.glow !== null ? (
              <>
                <Orb index={sh.glow} size={16} />
                {ORB_NAMES[((sh.glow % 6) + 6) % 6]}
              </>
            ) : (
              none
            )}
          </Fact>
        </>
      ) : (
        <>
          <Fact label="Puzzle">{sh.puzzleNumber !== null ? `#${sh.puzzleNumber}` : none}</Fact>
          <Fact label="Mode">{sh.mode === "hard" ? "Hard Mode" : sh.mode === "daily" ? "Daily" : none}</Fact>
          <Fact label="Result">{sh.correct === null ? none : sh.correct ? "Correct" : "Wrong"}</Fact>
          <Fact label="Percentile">{sh.percentile !== null ? `${Math.round(sh.percentile)}th` : none}</Fact>
          <Fact label="Rank">{sh.rank !== null ? `#${fmtInt(sh.rank)}` : none}</Fact>
        </>
      )}
      <Fact label="Streak">{sh.streak !== null ? `${fmtInt(sh.streak)} ${sh.streak === 1 ? "day" : "days"}` : none}</Fact>
      <Fact label="Format">{labelOf(FORMAT_LABEL, sh.format)}</Fact>
      <Fact label="Channel">{sh.channel ? labelOf(CHANNEL_LABEL, sh.channel) : <span className={pages.faint}>not completed</span>}</Fact>
      <Fact label="Language">{localeName(sh.locale) ?? none}</Fact>
      <Fact label="Created">{formatDateTime(sh.createdAt)}</Fact>
      <Fact label="Completed">{sh.completedAt ? formatDateTime(sh.completedAt) : <span className={pages.faint}>never (the sheet was cancelled)</span>}</Fact>
    </dl>
  );
}

function Totals({ detail: d }: { detail: ShareDetail }) {
  const t = d.totals;
  return (
    <div className={s.kpis} data-testid="share-kpis">
      <KpiTile
        palette="azure"
        label={
          <>
            Link <small>clicks</small>
          </>
        }
        {...readout(t.clicks)}
        foot={<span>{fmtInt(t.bots)} bots left out</span>}
      />
      <KpiTile
        palette="teal"
        label={
          <>
            Unique <small>visitors</small>
          </>
        }
        {...readout(t.uniques)}
        foot={<span>{fmtPct(rate(t.uniques, t.clicks))} of clicks</span>}
      />
      <KpiTile
        palette="violet"
        label={
          <>
            Link <small>previews</small>
          </>
        }
        {...readout(t.previews)}
        foot={<span>link-preview fetches, not clicks</span>}
      />
      <KpiTile
        palette="ember"
        label={
          <>
            App <small>opens</small>
          </>
        }
        {...readout(t.opens)}
        foot={<span>{fmtPct(t.opensPerClick, 1)} of clicks</span>}
      />
    </div>
  );
}
